import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bookServiceApi } from '@/services/api';

export interface ParagraphReactionDto {
    reaction: string;
    count: number;
    percentage: number;
}

export interface ParagraphCommentDto {
    interaction_id: number;
    user_id: number;
    user_name: string;
    user_avatar_url: string;
    content: string;
    created_at: string;
    user_reaction: string | null;
    reactions: any[];
    replies: ParagraphCommentDto[];
}

export interface ParagraphInteractionsResponse {
    section_id: number;
    paragraph_index: number;
    user_reaction: string | null;
    paragraph_reactions: ParagraphReactionDto[];
    comments: ParagraphCommentDto[];
    is_premium: boolean;
}

export interface PostInteractionPayload {
    section_id: number;
    paragraph_index: number;
    interaction_type: 'reaction' | 'comment';
    content: string;
    parent_interaction_id: number | null;
}

// Queries
export const useParagraphInteractionsQuery = (sectionId?: number, paragraphIndex?: number) => {
    return useQuery<ParagraphInteractionsResponse>({
        queryKey: ['paragraph_interactions', sectionId, paragraphIndex],
        queryFn: async () => {
            const res = await bookServiceApi.get<ParagraphInteractionsResponse>(`/novel/paragraph_interactions/${sectionId}/${paragraphIndex}`);
            if (res.success && res.data) return res.data;
            throw new Error(res.error || 'Failed to fetch interactions');
        },
        enabled: !!sectionId && paragraphIndex !== undefined,
    });
};

export const usePostParagraphInteraction = () => {
    const queryClient = useQueryClient();

    return useMutation<any, Error, PostInteractionPayload & { userProfile?: { name: string, avatar_url: string } }>({
        mutationFn: async ({ userProfile, ...payload }) => {
            const res = await bookServiceApi.post<any>('/novel/paragraph_interactions', payload);
            if (res.success) return res.data;
            throw new Error(res.error || 'Failed to post interaction');
        },
        onMutate: async (newInteraction) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({
                queryKey: ['paragraph_interactions', newInteraction.section_id, newInteraction.paragraph_index]
            });

            // Snapshot the previous value
            const previousInteractions = queryClient.getQueryData<ParagraphInteractionsResponse>(
                ['paragraph_interactions', newInteraction.section_id, newInteraction.paragraph_index]
            );

            // Optimistically update to the new value
            if (previousInteractions) {
                queryClient.setQueryData<ParagraphInteractionsResponse>(
                    ['paragraph_interactions', newInteraction.section_id, newInteraction.paragraph_index],
                    (old) => {
                        if (!old) return old;
                        const data = { ...old };

                        if (newInteraction.interaction_type === 'reaction') {
                            const oldUserReaction = data.user_reaction;
                            const newKey = newInteraction.content;

                            // 1. Handle user_reaction state
                            if (oldUserReaction === newKey) {
                                // Toggle off
                                data.user_reaction = null;
                            } else {
                                // Switch or new
                                data.user_reaction = newKey;
                            }

                            // 2. Handle reaction counts
                            const updatedReactions = [...data.paragraph_reactions];

                            // Decrease old if existed
                            if (oldUserReaction) {
                                const oldIdx = updatedReactions.findIndex(r => r.reaction === oldUserReaction);
                                if (oldIdx > -1) {
                                    updatedReactions[oldIdx] = {
                                        ...updatedReactions[oldIdx],
                                        count: Math.max(0, updatedReactions[oldIdx].count - 1)
                                    };
                                }
                            }

                            // Increase new if not toggled off
                            if (data.user_reaction === newKey) {
                                const newIdx = updatedReactions.findIndex(r => r.reaction === newKey);
                                if (newIdx > -1) {
                                    updatedReactions[newIdx] = {
                                        ...updatedReactions[newIdx],
                                        count: updatedReactions[newIdx].count + 1
                                    };
                                } else {
                                    // Add new reaction type to list
                                    updatedReactions.push({
                                        reaction: newKey,
                                        count: 1,
                                        percentage: 0 // Will be corrected by server anyway
                                    });
                                }
                            }
                            data.paragraph_reactions = updatedReactions;
                        } else if (newInteraction.interaction_type === 'comment' && !newInteraction.parent_interaction_id) {
                            // Basic optimistic comment (top level)
                            const newComment: ParagraphCommentDto = {
                                interaction_id: Math.random(), // Temporary ID
                                user_id: 0, // Will be corrected by server
                                user_name: newInteraction.userProfile?.name || 'Me',
                                user_avatar_url: newInteraction.userProfile?.avatar_url || '',
                                content: newInteraction.content,
                                created_at: new Date().toISOString(),
                                user_reaction: null,
                                reactions: [],
                                replies: []
                            };
                            data.comments = [newComment, ...data.comments];
                        }

                        return data;
                    }
                );
            }

            return { previousInteractions };
        },
        onError: (_err, newInteraction, context: any) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context?.previousInteractions) {
                queryClient.setQueryData(
                    ['paragraph_interactions', newInteraction.section_id, newInteraction.paragraph_index],
                    context.previousInteractions
                );
            }
        },
        onSettled: (_data, _error, variables) => {
            // Always refetch after error or success to make sure we are in sync with server
            queryClient.invalidateQueries({
                queryKey: ['paragraph_interactions', variables.section_id, variables.paragraph_index]
            });
        }
    });
};
